import React, { ReactNode } from 'react';
import { Box, SxProps, Theme, Typography } from '@mui/material';

type EmptyStateProps = {
    icon: ReactNode;
    title: string;
    description?: ReactNode;
    action?: ReactNode;
    /** `sm` for list columns and tabs, `md` for panels and page bodies. */
    size?: 'sm' | 'md';
    /** Dashed card look, used as a placeholder for a not-yet-selected detail panel. */
    variant?: 'plain' | 'dashed';
    sx?: SxProps<Theme>;
};

const SIZES = {
    sm: { circle: 44, icon: '1.375rem', title: '0.875rem', text: '0.8125rem', py: 5, gap: 1.5 },
    md: { circle: 64, icon: '1.875rem', title: '1rem', text: '0.875rem', py: 8, gap: 2 },
};

export const EmptyState = ({ icon, title, description, action, size = 'sm', variant = 'plain', sx }: EmptyStateProps) => {
    const s = SIZES[size];

    return (
        <Box
            role='status'
            sx={{
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center',
                px: 3, py: s.py, gap: s.gap, width: '100%',
                ...(variant === 'dashed' && {
                    borderRadius: '1rem', border: '1.5px dashed #DFE3E8', bgcolor: 'transparent',
                }),
                ...sx,
            }}
        >
            <Box sx={{
                width: s.circle, height: s.circle, borderRadius: '50%', flexShrink: 0,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                bgcolor: 'secondary.light', color: 'secondary.main',
                '& svg': { fontSize: s.icon },
            }}>
                {icon}
            </Box>
            <Box>
                <Typography sx={{ fontSize: s.title, fontWeight: 600, color: 'text.primary', lineHeight: 1.3 }}>{title}</Typography>
                {description && (
                    <Typography sx={{ fontSize: s.text, color: 'text.secondary', mt: 0.5, maxWidth: 360, mx: 'auto', lineHeight: 1.45 }}>
                        {description}
                    </Typography>
                )}
            </Box>
            {action && <Box sx={{ mt: 0.5 }}>{action}</Box>}
        </Box>
    );
};
